import type { ConversationsSummaryTimeframe } from "./conversationsSummaryTypes"

export const CONVERSATIONS_DATE_WINDOW_ALL = "all"

const CONVERSATIONS_DATE_WINDOWS = [
  { value: "24h", days: 1 },
  { value: "7d", days: 7 },
  { value: "30d", days: 30 },
] as const

type ConversationsDateWindow =
  (typeof CONVERSATIONS_DATE_WINDOWS)[number]["value"]

/** Accepts both the filter state's range and the transport query's looser one. */
interface DateWindowBounds {
  start?: Date | null
  end?: Date | null
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Tolerance when recognising a window in an applied range. The bounds are
 * stamped from one `Date`, so the span is exact; a minute absorbs any
 * serialization rounding on the way through the URL.
 */
const SPAN_TOLERANCE_MS = 60 * 1000

/**
 * Which window an applied range represents: `all` when unfiltered, the matching
 * window when the range spans one, and null for any other range — a custom one
 * from the date filter.
 *
 * Matching on the span rather than on how long ago the range was set keeps the
 * window recognised for as long as the range is applied.
 */
export function matchConversationsDateWindow(
  dateRange: DateWindowBounds,
): ConversationsDateWindow | typeof CONVERSATIONS_DATE_WINDOW_ALL | null {
  const { start, end } = dateRange

  if (!start && !end) {
    return CONVERSATIONS_DATE_WINDOW_ALL
  }
  if (!start || !end) {
    return null
  }

  const spanMs = end.getTime() - start.getTime()
  return (
    CONVERSATIONS_DATE_WINDOWS.find(
      (candidate) =>
        Math.abs(spanMs - candidate.days * MS_PER_DAY) <= SPAN_TOLERANCE_MS,
    )?.value ?? null
  )
}

/**
 * The summary window implied by the table's range, so Sofia describes what the
 * operator is looking at. Anything that is not one of the windows falls back to
 * the adaptive window the backend picks from conversation volume.
 */
export function toConversationsSummaryTimeframe(
  dateRange: DateWindowBounds | null | undefined,
): ConversationsSummaryTimeframe {
  const matched = dateRange ? matchConversationsDateWindow(dateRange) : null
  return matched === null || matched === CONVERSATIONS_DATE_WINDOW_ALL
    ? "auto"
    : matched
}

/**
 * The analysis window to request insights for, so the backend's current/prior
 * comparison covers the window Sofia is describing. Undefined for `auto`, which
 * leaves the backend to pick one from conversation volume.
 */
export function toConversationsAnalysisWindowDays(
  timeframe: ConversationsSummaryTimeframe,
): 1 | 7 | 30 | undefined {
  if (timeframe === "auto") {
    return undefined
  }
  return CONVERSATIONS_DATE_WINDOWS.find(
    (candidate) => candidate.value === timeframe,
  )?.days
}
